import { apiClient } from '../api';
import { Path } from '../api/backendUrl';
import { SupportTicketFormData } from '../utils/validation/supportTicketSchema';
import { logError } from '../utils/logger';

export interface SupportTicketResponse {
  success: boolean;
  message: string;
  ticket: {
    id: string;
    subject: string;
    category: string;
    priority: string;
    description: string;
    status: string;
    createdAt: string;
    contactId: string;
  };
}

export interface SupportTicketError {
  success: false;
  message: string;
  error?: string;
  details?: any;
}

/**
 * Create a new support ticket
 */
export const createSupportTicket = async (
  ticketData: SupportTicketFormData
): Promise<SupportTicketResponse> => {
  try {
    console.log('Creating support ticket with data:', ticketData);

    // Prepare the payload for the backend
    const payload = {
      name: ticketData.name,
      email: ticketData.email,
      phone: ticketData.phone || '', // Backend expects string, not undefined
      role: 'customer', // Add customer role
      subject: ticketData.subject,
      category: ticketData.category,
      priority: ticketData.priority,
      description: ticketData.description,
      attachments: ticketData.attachments || [], // Handle file attachments
    };

    console.log('Sending payload to backend:', payload);

    const response = await apiClient.post(
      `${import.meta.env.VITE_APP_BACKEND_USER}/${Path.createSupportTicket}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000, // 30 seconds timeout for file uploads
      }
    );

    console.log('Support ticket created successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating support ticket:', error);
    logError('Error creating support ticket:', { error });

    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const errorMessage =
        error.response.data?.message || 'Failed to create support ticket';
      const errorDetails = error.response.data;

      console.error('Server error response:', errorDetails);

      throw new Error(
        `${errorMessage}${errorDetails?.details ? `: ${errorDetails.details}` : ''}`
      );
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received:', error.request);
      throw new Error(
        'Unable to connect to the server. Please check your internet connection and try again.'
      );
    } else {
      // Something else happened
      console.error('Request setup error:', error.message);
      throw new Error(`Failed to create support ticket: ${error.message}`);
    }
  }
};

/**
 * Create a support ticket with file attachments
 * This function handles multipart form data for file uploads
 */
export const createSupportTicketWithAttachments = async (
  ticketData: SupportTicketFormData
): Promise<SupportTicketResponse> => {
  try {
    console.log('Creating support ticket with attachments:', ticketData);

    // Create FormData for file uploads
    const formData = new FormData();

    // Add text fields
    formData.append('name', ticketData.name);
    formData.append('email', ticketData.email);
    formData.append('phone', ticketData.phone || '');
    formData.append('role', 'customer'); // Add customer role
    formData.append('subject', ticketData.subject);
    formData.append('category', ticketData.category);
    formData.append('priority', ticketData.priority);
    formData.append('description', ticketData.description);

    // Add file attachments
    if (ticketData.attachments && ticketData.attachments.length > 0) {
      ticketData.attachments.forEach((file) => {
        if (file instanceof File) {
          formData.append(`attachments`, file);
        }
      });
    }

    console.log('Sending FormData to backend with attachments');

    const response = await apiClient.post(
      `${import.meta.env.VITE_APP_BACKEND_USER}/${Path.createSupportTicket}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 seconds timeout for file uploads
      }
    );

    console.log(
      'Support ticket with attachments created successfully:',
      response.data
    );
    return response.data;
  } catch (error: any) {
    console.error('Error creating support ticket with attachments:', error);
    logError('Error creating support ticket with attachments:', { error });

    // Handle different types of errors
    if (error.response) {
      const errorMessage =
        error.response.data?.message ||
        'Failed to create support ticket with attachments';
      const errorDetails = error.response.data;

      console.error('Server error response:', errorDetails);

      throw new Error(
        `${errorMessage}${errorDetails?.details ? `: ${errorDetails.details}` : ''}`
      );
    } else if (error.request) {
      console.error('No response received:', error.request);
      throw new Error(
        'Unable to connect to the server. Please check your internet connection and try again.'
      );
    } else {
      console.error('Request setup error:', error.message);
      throw new Error(`Failed to create support ticket: ${error.message}`);
    }
  }
};

/**
 * Helper function to determine if we need to use FormData for file uploads
 */
export const hasFileAttachments = (
  ticketData: SupportTicketFormData
): boolean => {
  return !!(ticketData.attachments && ticketData.attachments.length > 0);
};

/**
 * Main function to create support ticket - automatically chooses the right method
 */
export const submitSupportTicket = async (
  ticketData: SupportTicketFormData
): Promise<SupportTicketResponse> => {
  if (hasFileAttachments(ticketData)) {
    return createSupportTicketWithAttachments(ticketData);
  } else {
    return createSupportTicket(ticketData);
  }
};

// Support ticket interfaces for listing
export interface SupportTicket {
  id: string;
  subject: string;
  category: string;
  priority: string;
  description: string;
  status: string;
  createdTime: string;
  updatedTime?: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
}

export interface GetTicketsResponse {
  success: boolean;
  message: string;
  tickets: SupportTicket[];
}

/**
 * Get support tickets by email for a customer
 */
export const getTicketsByEmail = async (
  email: string,
  role: string = 'customer'
): Promise<GetTicketsResponse> => {
  try {
    console.log(`Fetching tickets for email: ${email}, role: ${role}`);

    const response = await apiClient.get(
      `${import.meta.env.VITE_APP_BACKEND_USER}/${Path.getTicketsByEmail}/${email}?role=${role}`,
      {
        timeout: 30000,
      }
    );

    console.log('Tickets fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching tickets:', error);
    logError('Error fetching tickets:', { error });

    // Handle different types of errors
    if (error.response) {
      const errorMessage =
        error.response.data?.message || 'Failed to fetch support tickets';
      const errorDetails = error.response.data;

      console.error('Server error response:', errorDetails);

      throw new Error(
        `${errorMessage}${errorDetails?.details ? `: ${errorDetails.details}` : ''}`
      );
    } else if (error.request) {
      console.error('No response received:', error.request);
      throw new Error(
        'Unable to connect to the server. Please check your internet connection and try again.'
      );
    } else {
      console.error('Request setup error:', error.message);
      throw new Error(`Failed to fetch support tickets: ${error.message}`);
    }
  }
};
